import type { AuroraDataset } from '@aurora-pvi/seed';
import { createScopeIndex } from './filters';
import type { RepositoryContext } from './context';
import { createPortfolioRepository } from './repositories/portfolio-repository';
import { createProductRepository } from './repositories/product-repository';
import { createDiscoverRepository } from './repositories/discover-repository';
import { createMappingRepository } from './repositories/mapping-repository';
import { createApplicationRepository } from './repositories/application-repository';
import { createDeliveryRepository } from './repositories/delivery-repository';
import { createValueRepository } from './repositories/value-repository';
import { createScenarioRepository } from './repositories/scenario-repository';
import { createIntegrationRepository } from './repositories/integration-repository';
import { createLineageRepository } from './repositories/lineage-repository';
import { createGovernanceRepository } from './repositories/governance-repository';
// These five live at the package root rather than under `repositories/`
// because they were authored alongside their screens; the factory shape is the
// same and the port does not care where a factory is declared.
import { createEstateRepository } from './estate';
import { createCollaborationRepository } from './collaboration';
import { createOutcomesRepository } from './outcomes';
// `identity-access`, not `identity`: `identity.ts` holds SEEDED_PERSONAS — who you
// can sign in as — which is a different question from what they may do.
import { createIdentityRepository } from './identity-access';
import { createProcessRepository } from './process';
import type { SeededPersona, Repositories } from './types';

/**
 * `@aurora-pvi/testing` — in-memory repositories over the synthetic dataset.
 *
 * These implement the same contract the API client will, including the async
 * signature, so replacing them in P1 is a provider swap rather than a page
 * rewrite. Nothing here imports a framework: the package is a pure function
 * from (dataset, user) to ten projections.
 */

export function createFixtureRepositories(
  dataset: AuroraDataset,
  currentUser: SeededPersona,
): Repositories {
  const context: RepositoryContext = {
    dataset,
    index: createScopeIndex(dataset),
    user: currentUser,
  };

  return {
    portfolio: createPortfolioRepository(context),
    products: createProductRepository(context),
    discover: createDiscoverRepository(context),
    mapping: createMappingRepository(context),
    applications: createApplicationRepository(context),
    delivery: createDeliveryRepository(context),
    value: createValueRepository(context),
    scenarios: createScenarioRepository(context),
    integrations: createIntegrationRepository(context),
    lineage: createLineageRepository(context),
    governance: createGovernanceRepository(context),
    estate: createEstateRepository(),
    collaboration: createCollaborationRepository(context),
    outcomes: createOutcomesRepository(),
    identity: createIdentityRepository(context),
    process: createProcessRepository(context),
  };
}

export { SEEDED_PERSONAS, DEFAULT_PERSONA, findSeededPersona } from './identity';

/**
 * Catalogue helpers, not repository calls: the estate and outcome pickers need
 * their option lists and a default selection before any query can run, so these
 * are read directly rather than fetched. Exported from the barrel so the whole
 * app has exactly one import path into this package.
 */
export { DEFAULT_COMPARISON, listPortfolioSnapshots } from './estate';
export { DEFAULT_OUTCOME_ID, listTraceableOutcomes } from './outcomes';
export { createScopeIndex, applyFilters, rollUpBenefit, rollUpCost } from './filters';
export type { ScopeIndex, ScopedData, CostRollup } from './filters';
export {
  FINANCE_MASKED_FIELDS,
  WORKFORCE_MASKED_FIELDS,
  buildMetadata,
  buildQuality,
  buildSourceStatus,
  chartPayload,
  currencyMetric,
  maskedFieldsFor,
  metricValue,
  moneyOrNull,
} from './metadata';
export { PAGE_SOURCES } from './context';
export type { RepositoryContext } from './context';
export type {
  ApplicationRefinement,
  CandidateListRow,
  DeliveryOverview,
  DeliveryReleaseRow,
  DeliveryTeamRow,
  SeededPersona,
  FixtureDataset,
  InvestmentOverview,
  InvestmentProductRow,
  PagedRows,
  ProductListRow,
  Repositories,
} from './types';
